import React from 'react';
import Keycloak, { type KeycloakInitOptions } from 'keycloak-js';

interface KeycloakProviderProps {
  children: React.ReactNode;
  url: string;
  realm: string;
  clientId: string;
  onLoad?: 'check-sso' | 'login-required' | 'none';
  redirectUri?: string;
}

interface KeycloakContextValue {
  keycloak: Keycloak;
  initialized: boolean;
}

const KeycloakContext = React.createContext<KeycloakContextValue | undefined>(undefined);

export const useKeycloak = () => {
  const context = React.useContext(KeycloakContext);

  if (!context) {
    throw new Error('useKeycloak must be used inside KeycloakProvider');
  }

  return context;
};

/**
 * Keycloak authentication provider
 * 
 * Wraps the application with Keycloak authentication context.
 * Automatically handles token refresh and provides authentication state.
 * 
 * @example
 * ```tsx
 * <KeycloakProvider
 *   url={import.meta.env.VITE_KEYCLOAK_URL}
 *   realm="y-sports"
 *   clientId="y-sports-management"
 * >
 *   <App />
 * </KeycloakProvider>
 * ```
 */
export function KeycloakProvider({
  children,
  url,
  realm,
  clientId,
  onLoad = 'check-sso',
  redirectUri,
}: KeycloakProviderProps) {
  // Use ref to ensure single instance even in StrictMode
  const keycloakRef = React.useRef<Keycloak | null>(null);
  
  if (!keycloakRef.current) {
    keycloakRef.current = new Keycloak({
      url,
      realm,
      clientId,
    });
  }

  const keycloak = keycloakRef.current;
  const initOptions = React.useMemo(
    () => {
      const options: KeycloakInitOptions = {
        checkLoginIframe: false,
        pkceMethod: 'S256',
        // Keep the callback URL stable after Keycloak adds code/state query params.
        redirectUri: redirectUri || `${window.location.origin}${window.location.pathname}`,
      };

      if (onLoad !== 'none') {
        options.onLoad = onLoad;
      }

      return options;
    },
    [onLoad, redirectUri]
  );

  const [initialized, setInitialized] = React.useState(false);
  const [authVersion, setAuthVersion] = React.useState(0);
  const initStartedRef = React.useRef(false);
  const notifyAuthChanged = React.useCallback(() => {
    setAuthVersion((version) => version + 1);
  }, []);
  React.useEffect(() => {
    if (initStartedRef.current) {
      return;
    }

    initStartedRef.current = true;
    let cancelled = false;

    keycloak.onAuthSuccess = () => {
      notifyAuthChanged();
    };
    keycloak.onAuthRefreshSuccess = () => {
      notifyAuthChanged();
    };
    keycloak.onAuthLogout = () => {
      notifyAuthChanged();
    };
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).then(notifyAuthChanged).catch((error) => {
        console.error('[Keycloak] Token refresh failed:', error);
        keycloak.clearToken();
        notifyAuthChanged();
      });
    };

    const timeout = new Promise<boolean>((_, reject) => {
      window.setTimeout(() => reject(new Error('Keycloak init timed out')), 15000);
    });

    Promise.race([keycloak.init(initOptions), timeout])
      .then((authenticated) => {
        console.info('[Keycloak] Initialized', { authenticated });
        notifyAuthChanged();
      })
      .catch((error) => {
        console.error('[Keycloak] Init failed:', error);
      })
      .finally(() => {
        if (!cancelled) {
          setInitialized(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initOptions, keycloak, notifyAuthChanged]);

  const contextValue = React.useMemo(
    () => ({ keycloak, initialized }),
    [authVersion, initialized, keycloak]
  );

  return (
    <KeycloakContext.Provider value={contextValue}>
      {children}
    </KeycloakContext.Provider>
  );
}
